import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  Deployment,
  Location,
  OrgUserName,
  PaginatedResponse,
  ApprovalStatus,
  DeploymentAction,
} from '@/lib/types';

export interface DeploymentFilters {
  page?: number;
  page_size?: number;
  action?: DeploymentAction | '';
  overdue?: boolean | '';
  assigned_to_user?: number | '';
  location?: number | '';
  date_after?: string;
  date_before?: string;
  approval_status?: ApprovalStatus | '';
  asset?: number | '';
  project?: number | '';
  active?: boolean | '';
  ordering?: string;
}

export interface DeploymentWithDetails extends Deployment {
  asset_name?: string;
  asset_serial_number?: string;
  assigned_to_user_name?: string;
  location_name?: string;
  user_name?: string;
}

export function useDeployments(filters: DeploymentFilters = {}) {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );

  return useQuery({
    queryKey: queryKeys.deployments.list(cleanFilters),
    queryFn: async (): Promise<PaginatedResponse<DeploymentWithDetails>> => {
      const response = await apiClient.get<PaginatedResponse<DeploymentWithDetails>>(
        '/api/v1/deployments/',
        { params: cleanFilters }
      );
      return response.data;
    },
  });
}

export function useApproveDeployment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number): Promise<Deployment> => {
      const response = await apiClient.patch<Deployment>(`/api/v1/deployments/${id}/`, {
        approval_status: 'approved',
      });
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.deployments.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.deployments.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
    },
  });
}

export function useRejectDeployment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number): Promise<Deployment> => {
      const response = await apiClient.patch<Deployment>(`/api/v1/deployments/${id}/`, {
        approval_status: 'rejected',
      });
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.deployments.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.deployments.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
    },
  });
}

export function useLocations() {
  return useQuery({
    queryKey: queryKeys.locations.lists(),
    queryFn: async (): Promise<Location[]> => {
      const response = await apiClient.get<PaginatedResponse<Location>>('/api/v1/cf-locations/', {
        params: { page_size: 100 },
      });
      return response.data.results;
    },
  });
}

/**
 * Names-only org member list for assign-to pickers. Uses
 * /api/v1/users/names/ which Gear Managers may read; the full
 * /api/v1/users/ endpoint stays admin-only.
 */
export function useOrganizationUsers() {
  return useQuery({
    queryKey: queryKeys.users.lists(),
    queryFn: async (): Promise<OrgUserName[]> => {
      const response = await apiClient.get<OrgUserName[]>('/api/v1/users/names/');
      return response.data;
    },
  });
}
